import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  AltRouteOutlined,
  HandshakeOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';

import RegisterForm from '../sections/auth/login/RegisterForm';

const REGISTER_INFO_CARDS = [
  {
    title: 'Supplier and Client Collaboration',
    description:
      'Coordinate suppliers, logistics teams, and client deliveries on a shared operational platform.',
    icon: HandshakeOutlined,
    accent: '#22d3ee',
  },
  {
    title: 'Traceable Order Lifecycle',
    description:
      'Track each order stage from planning and allocation through dispatch and completion.',
    icon: AltRouteOutlined,
    accent: '#f59e0b',
  },
  {
    title: 'Controlled Compliance Flow',
    description:
      'Maintain evidence, approvals, and audit-friendly records across customs and finance operations.',
    icon: VerifiedUserOutlined,
    accent: '#4ade80',
  },
];

const REGISTER_STEPS = [
  'Create Profile',
  'Company Setup',
  'Role Assignment',
  'Operational Access',
];

export default function RegisterPage() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Register</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
          backgroundImage:
            "linear-gradient(110deg, rgba(4, 18, 34, 0.86) 0%, rgba(9, 34, 57, 0.76) 50%, rgba(11, 25, 42, 0.85) 100%), url('/assets/background.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          px: 2,
          py: { xs: 3, md: 6 },
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={3} alignItems="stretch">
            <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 1, md: 1 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.3)',
                  backgroundColor: 'rgba(255,255,255,0.93)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Stack spacing={2}>
                  <Box
                    component="img"
                    alt="Honey Badger"
                    src="/assets/images/hb-logo-only-dark.png"
                    sx={{ width: 82, height: 82, alignSelf: 'center' }}
                  />
                  <Typography
                    component="h1"
                    variant="h4"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 700,
                      fontFamily: '"Rajdhani", "IBM Plex Sans", sans-serif',
                    }}
                  >
                    Create your HoneyBadger account
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    Register to access operational workflows, reports, and end-to-end logistics execution.
                  </Typography>

                  <RegisterForm />

                  <Typography sx={{ textAlign: 'center' }}>
                    Already have an account?{' '}
                    <Link
                      sx={{ cursor: 'pointer' }}
                      variant="body2"
                      onClick={() => {
                        navigate('/login');
                      }}
                    >
                      Log in
                    </Link>
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }} sx={{ order: { xs: 2, md: 2 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.18)',
                  background:
                    'linear-gradient(160deg, rgba(11,27,46,0.86) 0%, rgba(8,21,35,0.82) 100%)',
                  backdropFilter: 'blur(8px)',
                  color: '#E5F4FF',
                }}
              >
                <Box
                  component="img"
                  alt="HoneyBadger"
                  src="/assets/images/hb-wordmark-light.png"
                  sx={{ height: 42, mb: 2 }}
                />

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.15,
                    mb: 1.5,
                    fontFamily: '"Rajdhani", "IBM Plex Sans", sans-serif',
                  }}
                >
                  Why teams onboard to HoneyBadger
                </Typography>
                <Typography sx={{ color: 'rgba(229,244,255,0.86)', mb: 2.5 }}>
                  HoneyBadger standardizes how fuel supply operations are planned, executed, and monitored.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
                  {REGISTER_STEPS.map((step) => (
                    <Chip
                      key={step}
                      label={step}
                      size="small"
                      sx={{
                        color: '#D4EBFF',
                        backgroundColor: 'rgba(73, 148, 206, 0.2)',
                        border: '1px solid rgba(122, 180, 224, 0.34)',
                      }}
                    />
                  ))}
                </Stack>

                <Divider sx={{ borderColor: 'rgba(170,210,240,0.2)', mb: 2 }} />

                <Stack spacing={1.5}>
                  {REGISTER_INFO_CARDS.map((card) => {
                    const Icon = card.icon;
                    return (
                      <Paper
                        key={card.title}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          borderLeft: `4px solid ${card.accent}`,
                          backgroundColor: 'rgba(255,255,255,0.06)',
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75 }}>
                          <Icon sx={{ color: card.accent }} />
                          <Typography sx={{ fontWeight: 600, color: '#E5F4FF' }}>
                            {card.title}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: 'rgba(229,244,255,0.82)' }}>
                          {card.description}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
